import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import COLORS from "../../lib/colors";
import { PageTitle } from "../../components/Layout";
import EditorialCalendar, { fmtDayLong } from "../../components/EditorialCalendar";
import PostDetailModal from "../../components/PostDetailModal";
import { useConfirm } from "../../components/ConfirmDialog";
import * as Icons from "lucide-react";

const localToday = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const errText = (e, fallback) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export default function AdminCalendar() {
  const { confirm } = useConfirm();
  const [posts, setPosts] = useState([]);
  const [clients, setClients] = useState([]);
  const [collabs, setCollabs] = useState([]);
  const [clientFilter, setClientFilter] = useState("");
  const [modal, setModal] = useState(null); // { mode: "create"|"edit", post?, draft? }

  const load = useCallback(() => api.get("/admin/posts").then((r) => setPosts(r.data)), []);
  useEffect(() => {
    load();
    api.get("/admin/clients").then((r) => setClients(r.data));
    api.get("/admin/collaborators").then((r) => setCollabs(r.data));
  }, [load]);

  const filtered = clientFilter ? posts.filter((p) => p.client_id === clientFilter) : posts;
  const clientNames = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.name])), [clients]);

  const handleCreate = async (date, time) => {
    if (date < localToday() && !(await confirm({ title: "Cette date est déjà passée", message: "Créer quand même une publication à cette date ?", confirmLabel: "Créer quand même" }))) return;
    setModal({ mode: "create", draft: { client_id: clientFilter, date, time } });
  };

  // Glisser-déposer : la publication change de place tout de suite, le serveur suit ; « Annuler » la remet.
  const setPlace = (id, at) => setPosts((ps) => ps.map((p) => (p.id === id ? { ...p, date: at.date, time: at.time || null } : p)));
  const handleMove = async (post, to) => {
    const from = { date: (post.date || "").slice(0, 10), time: post.time || "" };
    if (from.date === to.date && from.time === (to.time || "")) return;
    setPlace(post.id, to);
    try {
      await api.put(`/admin/posts/${post.id}`, { date: to.date, time: to.time || "" });
      toast.success(`Publication déplacée au ${fmtDayLong(to.date)}${to.time ? ` à ${to.time}` : ", sans heure"}.`, {
        action: {
          label: "Annuler",
          onClick: async () => {
            setPlace(post.id, from);
            try { await api.put(`/admin/posts/${post.id}`, { date: from.date, time: from.time }); }
            catch { toast.error("Impossible d'annuler le déplacement."); load(); }
          },
        },
        actionButtonStyle: { background: COLORS.marine, color: "#fff" },
      });
    } catch (e) {
      setPlace(post.id, from);
      toast.error(errText(e, "Déplacement impossible."));
    }
  };

  return (
    <div>
      <PageTitle overline="Communication" title="Calendrier éditorial" />
      <EditorialCalendar
        posts={filtered}
        clientNames={clientFilter ? null : clientNames}
        onPostClick={(p) => setModal({ mode: "edit", post: p })}
        onCreate={handleCreate}
        onMove={handleMove}
        toolbarExtra={<>
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="calendar-client-filter" aria-label="Filtrer par entreprise"
            className="h-[38px] rounded-sm border border-marine/25 bg-white pl-3 text-[13px] text-marine">
            <option value="">Toutes les entreprises</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {/* Visible dans toutes les vues (recette du 15/10 ; décision de Mélilia, question 11) : la vue semaine, celle
              d'ouverture, n'indiquait pas comment créer une publication. Date du jour, modifiable dans la fenêtre. */}
          <button type="button" onClick={() => handleCreate(localToday(), "")} data-testid="calendar-new-post"
            className="h-[38px] inline-flex items-center gap-1.5 rounded-sm bg-marine text-white px-3.5 text-[13px] font-semibold hover:bg-marine-hover transition-colors">
            <Icons.Plus size={16} /> Nouvelle publication
          </button>
        </>}
      />

      {modal && (
        <PostDetailModal
          mode={modal.mode}
          post={modal.post}
          draft={modal.draft}
          clients={clients}
          collaborators={collabs}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
